/**
 * Customer Detail — Bill-To + Ship-To address book (Feb 2026).
 *
 * Opened from TechOps → Buyers when the user clicks a customer name.
 * Renders the buyer profile plus two grouped sections of address
 * cards with add/edit/delete/set-default. RBAC follows buyer-CRUD
 * (the underlying endpoints reuse the buyer access model — no new
 * permission entity — per the feature spec).
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Truck, Plus, Pencil, Trash2, Star, StarOff, Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import ZohoCustomerLinkDialog from "@/components/ZohoCustomerLinkDialog";
import KamAssignDialog from "@/components/KamAssignDialog";
import PermButton from "@/components/PermButton";
import { AlertTriangle, Link2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const BLANK_FORM = {
  address_id: null,
  address_type: "BILL_TO",
  label: "",
  gstin: "",
  contact_name: "",
  contact_phone: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  pincode: "",
  country: "India",
  is_default: false,
};

export default function CustomerDetail() {
  const { buyerId } = useParams();
  const navigate = useNavigate();
  const [buyer, setBuyer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Feb 2026 — Zoho link dialog state (open when banner action clicked).
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  // Sep 2026 — Assign KAM dialog (shared `KamAssignDialog`). Customer
  // Detail is the sole KAM-assignment entry point; the Buyer Management
  // list keeps only the read-only KAM column.
  const [showKamDialog, setShowKamDialog] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/buyers/${buyerId}`, auth());
      setBuyer(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load customer");
    } finally {
      setLoading(false);
    }
  }, [buyerId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = (address_type) => {
    setForm({ ...BLANK_FORM, address_type });
    setShowForm(true);
  };

  const openEdit = (a) => {
    setForm({
      address_id: a.address_id,
      address_type: a.address_type,
      label: a.label || "",
      gstin: a.gstin || "",
      contact_name: a.contact_name || "",
      contact_phone: a.contact_phone || "",
      line1: a.line1 || "",
      line2: a.line2 || "",
      city: a.city || "",
      state: a.state || "",
      pincode: a.pincode || "",
      country: a.country || "India",
      is_default: !!a.is_default,
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.label.trim()) { toast.error("Label is required"); return; }
    if (!form.state.trim()) { toast.error("State is required"); return; }
    // Feb 2026 (AC7) — GSTIN is required on every new bill-to /
    // ship-to address. Existing entries saved without one are
    // unaffected (AC8): the edit path only enforces this when the
    // field is explicitly cleared.
    if (!form.gstin.trim()) { toast.error("GSTIN is required"); return; }
    setSaving(true);
    try {
      if (form.address_id) {
        // Edit — address_type immutable server-side.
        const { address_id, address_type: _at, ...payload } = form;
        await axios.put(`${API}/buyers/${buyerId}/addresses/${address_id}`, payload, auth());
        toast.success("Address updated");
      } else {
        const { address_id: _aid, ...payload } = form;
        await axios.post(`${API}/buyers/${buyerId}/addresses`, payload, auth());
        toast.success("Address added");
      }
      setShowForm(false);
      await load();
    } catch (e) {
      const d = e.response?.data?.detail || e.originalDetail;
      const msg = (d && typeof d === "object" ? (d.message || JSON.stringify(d)) : d) || e.message;
      toast.error(`Save failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const setDefault = async (a) => {
    try {
      await axios.post(`${API}/buyers/${buyerId}/addresses/${a.address_id}/set-default`, {}, auth());
      toast.success(`Default ${a.address_type === "BILL_TO" ? "Bill-To" : "Ship-To"} set`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to set default");
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/buyers/${buyerId}/addresses/${deleteTarget.address_id}`, auth());
      toast.success("Address removed");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    }
  };

  if (loading) return <div className="p-8 text-muted-foreground">Loading customer…</div>;
  if (!buyer) return <div className="p-8 text-red-600">Customer not found.</div>;

  const active = (buyer.addresses || []).filter(a => (a.status || "ACTIVE") === "ACTIVE");
  const bill = active.filter(a => a.address_type === "BILL_TO");
  const ship = active.filter(a => a.address_type === "SHIP_TO");

  return (
    <div className="p-6 space-y-6" data-testid="customer-detail-page">
      {/* Feb 2026 — Zoho-not-linked banner. Rendered as the FIRST
          child so it lands above the buyer name/header. Dispatches
          for an unlinked buyer are blocked (dispatch push resolves
          `customer_zoho_id` and fails without it), so this is the
          top-of-page "you need to act" surface. The banner offers
          the search-and-pick action inline — only when a search
          returns no match does the copy point at contacting an admin. */}
      {!buyer.customer_zoho_id && (
        <div
          className="border-2 border-red-600 bg-red-50 rounded-lg p-4 flex items-start gap-3"
          data-testid="buyer-zoho-not-linked-banner"
        >
          <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-red-900 font-semibold">
              This buyer is not linked to a Zoho customer.
            </div>
            <div className="text-sm text-red-800 mt-1">
              Dispatches for this buyer are blocked until it&apos;s linked.
              Try to find the matching Zoho customer below — if none of
              the results look right, contact an admin to create or
              reconcile this customer in Zoho.
            </div>
          </div>
          <Button
            variant="default"
            className="bg-red-600 hover:bg-red-700 text-white flex-shrink-0"
            onClick={() => setShowLinkDialog(true)}
            data-testid="buyer-zoho-link-banner-btn"
          >
            <Link2 className="w-4 h-4 mr-2" /> Link to Zoho
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate("/buyer-management")}
                data-testid="back-to-buyers">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Buyers
        </Button>
        <div className="text-xs text-muted-foreground">/ Customers /</div>
        <Link to="/buyer-management" className="text-xs text-blue-700 hover:underline">Buyers</Link>
      </div>

      {/* Profile */}
      <div className="border rounded-lg p-5 bg-white" data-testid="customer-profile">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-slate-600" />
              <h1 className="text-2xl font-bold" data-testid="customer-name">{buyer.name}</h1>
              <Badge variant="outline" className="font-mono">{buyer.customer_code}</Badge>
              <Badge variant={buyer.status === "ACTIVE" ? "default" : "secondary"}>{buyer.status}</Badge>
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><span className="text-muted-foreground">GST:</span> <span className="font-mono">{buyer.gst || "—"}</span></div>
              <div><span className="text-muted-foreground">Email:</span> {buyer.email || "—"}</div>
              <div><span className="text-muted-foreground">Phone:</span> <span className="font-mono">{buyer.phone_no || "—"}</span></div>
              <div><span className="text-muted-foreground">POC:</span> {buyer.poc_name || "—"}</div>
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">KAM:</span>
                <span data-testid="customer-kam-value">
                  {buyer.current_kam ? buyer.current_kam.name : "—"}
                </span>
                <PermButton
                  entity="Buyer"
                  action="UPDATE"
                  variant="ghost"
                  size="sm"
                  className="h-auto p-1"
                  onClick={() => setShowKamDialog(true)}
                  data-testid="customer-kam-edit-btn"
                >
                  <Pencil className="w-3 h-3" />
                </PermButton>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Two address sections */}
      <AddressSection
        title="Bill-To Addresses" icon={<Building2 className="w-4 h-4" />}
        addresses={bill} address_type="BILL_TO"
        onAdd={() => openCreate("BILL_TO")}
        onEdit={openEdit}
        onDelete={setDeleteTarget}
        onSetDefault={setDefault}
      />
      <AddressSection
        title="Ship-To Addresses" icon={<Truck className="w-4 h-4" />}
        addresses={ship} address_type="SHIP_TO"
        onAdd={() => openCreate("SHIP_TO")}
        onEdit={openEdit}
        onDelete={setDeleteTarget}
        onSetDefault={setDefault}
      />

      {/* Add/Edit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="address-form-dialog">
          <DialogHeader>
            <DialogTitle>
              {form.address_id ? "Edit" : "Add"} {form.address_type === "BILL_TO" ? "Bill-To" : "Ship-To"} Address
            </DialogTitle>
            <DialogDescription>
              Address labels must be unique per type on this customer. Setting default clears the previous default of the same type.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Label *</Label>
              <Input
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="e.g. HQ, Warehouse 1"
                data-testid="addr-label"
              />
            </div>
            <div>
              <Label>Address Type</Label>
              <Select
                value={form.address_type}
                onValueChange={(v) => setForm({ ...form, address_type: v })}
                disabled={!!form.address_id}
              >
                <SelectTrigger data-testid="addr-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="BILL_TO">Bill-To</SelectItem>
                  <SelectItem value="SHIP_TO">Ship-To</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>GSTIN *</Label>
              <Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })}
                     placeholder="15-digit GSTIN" className="font-mono" data-testid="addr-gstin" />
            </div>
            <div>
              <Label>Contact Name</Label>
              <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
            </div>
            <div>
              <Label>Contact Phone</Label>
              <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="font-mono" />
            </div>
            <div>
              <Label>Country</Label>
              <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Address Line 1</Label>
              <Input value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })}
                     data-testid="addr-line1" />
            </div>
            <div className="col-span-2">
              <Label>Address Line 2</Label>
              <Input value={form.line2} onChange={(e) => setForm({ ...form, line2: e.target.value })} />
            </div>
            <div>
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
            <div>
              <Label>State *</Label>
              <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}
                     placeholder="e.g. Karnataka" data-testid="addr-state" />
            </div>
            <div>
              <Label>Pincode</Label>
              <Input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })}
                     className="font-mono" />
            </div>
            <div className="flex items-end gap-2">
              <input
                id="is_default-check" type="checkbox"
                checked={form.is_default}
                onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
                data-testid="addr-is-default"
              />
              <Label htmlFor="is_default-check" className="cursor-pointer">Set as default for this type</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.label || !form.state || !form.gstin.trim()} data-testid="save-address-btn">
              {saving ? "Saving..." : (form.address_id ? "Save changes" : "Add address")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this address?</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{deleteTarget?.label}&quot; will be marked INACTIVE. If it was the default,
              another active address of the same type is promoted automatically.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={remove}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Feb 2026 — Zoho customer search-and-pick dialog.
          Must live in the parent that owns `showLinkDialog` /
          `setShowLinkDialog` / `buyer` / `load` — placing it inside
          `AddressSection` (which knows none of those) crashes the
          page with a ReferenceError on every render. Regression
          guard: `__tests__/customerDetailZohoDialogPlacement.test.js`. */}
      <ZohoCustomerLinkDialog
        open={showLinkDialog}
        onOpenChange={setShowLinkDialog}
        buyer={buyer}
        onLinked={() => load()}
        testIdPrefix="customer-detail-zoho-link"
      />

      {/* Sep 2026 — shared Assign KAM dialog (same component + endpoints
          as the shipped Buyer Management flow). Reload on save so the
          header-grid KAM field reflects the change immediately. */}
      <KamAssignDialog
        open={showKamDialog}
        onOpenChange={setShowKamDialog}
        buyer={buyer}
        onSaved={() => load()}
      />
    </div>
  );
}

function AddressSection({ title, icon, addresses, address_type, onAdd, onEdit, onDelete, onSetDefault }) {
  return (
    <div className="border rounded-lg p-5 bg-white" data-testid={`address-section-${address_type}`}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold flex items-center gap-2">{icon} {title}</h2>
        <Button size="sm" onClick={onAdd} data-testid={`add-address-${address_type}`}>
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>
      {addresses.length === 0 ? (
        <div className="text-sm text-muted-foreground p-6 text-center border rounded bg-slate-50">
          No {title.toLowerCase()} yet. Click <b>Add</b> to create one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {addresses.map(a => (
            <div key={a.address_id} className="border rounded p-3 relative" data-testid={`addr-card-${a.label}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold flex items-center gap-2">
                    {a.label}
                    {a.is_default && (
                      <Badge variant="default" className="text-[10px]" data-testid={`default-badge-${a.label}`}>
                        <Star className="w-3 h-3 mr-1" /> Default
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs font-mono text-muted-foreground mt-0.5">
                    {a.gstin || "—"}
                  </div>
                </div>
                <div className="flex gap-1">
                  {!a.is_default && (
                    <Button size="icon" variant="ghost" title="Set as default"
                            onClick={() => onSetDefault(a)}
                            data-testid={`set-default-${a.label}`}>
                      <StarOff className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" onClick={() => onEdit(a)}
                          data-testid={`edit-address-${a.label}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => onDelete(a)}
                          data-testid={`delete-address-${a.label}`}>
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </Button>
                </div>
              </div>
              <div className="mt-2 text-sm space-y-0.5">
                <div>{a.line1}</div>
                {a.line2 && <div className="text-muted-foreground">{a.line2}</div>}
                <div className="text-muted-foreground text-xs flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {[a.city, a.state, a.pincode].filter(Boolean).join(", ")}
                  {a.country && a.country !== "India" ? ` · ${a.country}` : ""}
                </div>
                {(a.contact_name || a.contact_phone) && (
                  <div className="text-xs text-muted-foreground">
                    {a.contact_name}{a.contact_name && a.contact_phone ? " · " : ""}
                    <span className="font-mono">{a.contact_phone}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
